import { ApiParams, ApiResps, apiCall } from '~/providers';
import { OperationType, StatusType } from '../StatusCard';
import { CesEvent, HealthStatus, State } from './types';
import { identifyDutIrCommands } from '~/pages/Analysis/DUTs/EnvironmentRealTime';
import { RemoteControlReason } from '~/providers/types/remoteControl';
import Hotjar from '@hotjar/browser';

export async function getMachineInfo(reqParams: ApiParams['/remote-control/get-machine-info']): Promise<ApiResps['/remote-control/get-machine-info'] | undefined> {
  try {
    return await apiCall('/remote-control/get-machine-info', reqParams);
  } catch (error) {
    console.log(error);
    return undefined;
  }
}

export async function setMachineConfig(reqParams: ApiParams['/remote-control/set-config']): Promise<ApiResps['/remote-control/set-config'] | undefined> {
  try {
    const result = await apiCall('/remote-control/set-config', reqParams);
    if (process.env.REACT_APP_HOTJAR_ID && process.env.REACT_APP_HOTJAR_VERSION) {
      Hotjar.event('botao_salvar_alteracoes_acionado_com_sucesso');
    }
    return result;
  } catch (error) {
    console.log(error);
    return undefined;
  }
}

export const CES_SESSION_PREFIX = 'ces_';

export function fireCesEvent(eventName: CesEvent): void {
  try {
    const key = `${CES_SESSION_PREFIX}${eventName}`;
    if (sessionStorage.getItem(key)) return;
    if (process.env.REACT_APP_HOTJAR_ID && process.env.REACT_APP_HOTJAR_VERSION) {
      Hotjar.event(eventName);
    }
    sessionStorage.setItem(key, '1');
  } catch (error) {
    console.error(error);
  }
}

export async function getIrCommandsList(devId: string): Promise<ApiResps['/get-dut-ircodes-list']> {
  try {
    return await apiCall('/get-dut-ircodes-list', { devId });
  } catch (error) {
    console.log(error);
    return { list: [], dutCommandTypes: [] };
  }
}

type Machine = {
  operation: State | null;
  rssi: number | undefined;
  status: 'ONLINE' | 'OFFLINE';
}

export function verifyConnection(rssi: number | undefined, status: 'ONLINE' | 'OFFLINE'): boolean {
  return !!rssi || status === 'ONLINE';
}

const offStates = ['forbid', 'disabling'];
const coolStates = ['allow', 'enabling', 'cool', 'eco'];
const fanStates = ['onlyfan', 'fan'];

export function verifyStatus(machine: Machine, isDamAut?: boolean): StatusType | undefined {
  if (!verifyConnection(machine.rssi, machine.status)) return;
  const stateTelemetry: State | null = machine.operation;

  if (offStates.some((x) => x === stateTelemetry)) return 'off';

  if ((!stateTelemetry || stateTelemetry === 'thermostat') && !isDamAut && verifyConnection(machine.rssi, machine.status)) return 'freeMode';

  if ([...coolStates, ...fanStates].some((x) => x === stateTelemetry)) return 'on';
}

export function verifyOperation(machine: Machine): OperationType | undefined {
  if (!verifyConnection(machine.rssi, machine.status)) return;

  const stateTelemetry: State | null = machine.operation;

  if (coolStates.some((x) => x === stateTelemetry)) return 'cooling';

  if (fanStates.some((x) => x === stateTelemetry)) return 'ventilating';
}

function foundExactClosestSetpoint(
  availableSetpoints: {id: string, setpoint: number}[],
  currentSetpoint: number,
): {
  setpoint: number,
  id: string,
} | undefined {
  const equalSetpoint = availableSetpoints.find(({ setpoint }) => setpoint === currentSetpoint);

  if (equalSetpoint) {
    return equalSetpoint;
  }

  const foundAboveSetpoint = availableSetpoints.find(({ setpoint }) => setpoint === currentSetpoint + 1);
  const foundBelowSetpoint = availableSetpoints.find(({ setpoint }) => setpoint === currentSetpoint - 1);

  if (foundAboveSetpoint || foundBelowSetpoint) {
    return foundAboveSetpoint ?? foundBelowSetpoint;
  }

  return undefined;
}

/**
 *
 * Function to get setpoint by availableSetpoints
 * - Get the exact setpoint
 * - If not exists, get the closest setpoint (1ºC above or below)
 * - If not exists, get the closest above
 * - If not exists, get the closest below
 *
 * @export
 * @param {{id: string, setpoint: number}[]} availableSetpoints - Setpoints with IR Code
 * @param {number} [currentSetpoint] - Setpoint selected
 * @return {*}  {({
 *   setpoint: number,
 *   id: string,
 * } | undefined)}
 */
export function getIRSetpointCloseOrBelow(
  availableSetpoints: {id: string, setpoint: number}[],
  currentSetpoint?: number,
): {
  setpoint: number,
  id: string,
} | undefined {
  if (availableSetpoints.length === 0 || !currentSetpoint) {
    return undefined;
  }

  const closestSetpoint = foundExactClosestSetpoint(availableSetpoints, currentSetpoint);

  if (closestSetpoint) {
    return closestSetpoint;
  }

  let closestBelow: {
    setpoint: number,
    id: string,
  } | undefined;

  for (const availableSetpoint of availableSetpoints) {
    if (availableSetpoint.setpoint > currentSetpoint) {
      return availableSetpoint;
    }

    closestBelow = availableSetpoint;
  }

  return closestBelow;
}

export function parseOperationToType(operation: string): OperationType {
  switch (operation) {
    case 'cool': return 'cooling';
    case 'fan': return 'ventilating';
    default: throw new Error(`Operation type not found: ${operation}`);
  }
}

export async function getSortedDutSetpoints(deviceCode: string): Promise<{ id: string, setpoint: number}[]> {
  const response = await getIrCommandsList(deviceCode);
  const irCommands = identifyDutIrCommands(response.list)
    .filter((command) => {
      const cmdSetpoint = Number(command?.cmdName?.split(':')[1]) || null;
      return !!cmdSetpoint;
    });
  const setpoints: { id: string, setpoint: number}[] = irCommands.map((command) => ({
    id: command.IR_ID as string,
    setpoint: Number(command?.cmdName?.split(':')[1]),
  })).sort((a, b) => a.setpoint - b.setpoint);

  return setpoints;
}

export function getDefaultSetpointOnChangeMode(limits: {tMin: number, tMax: number}, currentSetpoint: number): number {
  const maxDefaultSetpoint = 25;
  const minDefaultSetpoint = 20;

  let max = maxDefaultSetpoint;
  let min = minDefaultSetpoint;

  if (max > limits.tMax) {
    max = limits.tMax;
  }

  if (min < limits.tMin) {
    min = limits.tMin;
  }

  if (currentSetpoint > max) return max;

  if (currentSetpoint < min) return min;

  return currentSetpoint;
}

export function formatHealthIndexToStatus(healthIndex: number): HealthStatus | undefined {
  switch (healthIndex) {
    case 25: return HealthStatus.INOPERATIVE;
    case 50: return HealthStatus.ALERT;
    case 75: return HealthStatus.ALERT;
    case 100: return HealthStatus.NO_ALERT;
    default: return undefined;
  }
}

export function parseReasonLabel(reason: RemoteControlReason): string {
  switch (reason) {
    case RemoteControlReason.COLD: return 'sensacaoDeFrio';
    case RemoteControlReason.HOT: return 'sensacaoDeCalor';
    case RemoteControlReason.MAINTENANCE: return 'manutencaoDeEquipamento';
    case RemoteControlReason.OUT_SCHEDULE: return 'unidadeAbrindoForaHorario';
    case RemoteControlReason.OTHER: return 'outro';
    default: throw new Error(`Reason ${reason} not found.`);
  }
}
